/**
 * Stance — JS entry point
 *
 * Split into two tiers to keep the ~28 KiB GSAP bundle off the critical path:
 *
 *  • Eager — GSAP-free modules needed for first paint or immediate interaction
 *    (nav, tabs, demo picker, the ecosystem diagram, the mobile drawer). None
 *    import GSAP, so none of them pull it into the initial bundle.
 *
 *  • Deferred — the GSAP-backed motion system (scroll reveals, smooth scroll,
 *    hover chips, the solutions scroller). Not needed for first paint, so it's
 *    loaded on the browser's idle callback or the user's first interaction,
 *    whichever comes first. The hero animates via CSS instead, and motion.js
 *    skips any element already on screen when it initializes (no flash).
 */

// ── Eager (no GSAP) ──────────────────────────────────────────────────────────
import './sidebar-progress.js';
import './sidebar-collapse.js';
import './mobile-nav.js';
import './smooth-scroll.js';
import './tabs.js';
import './sidebar-cta-variant.js';
import './cta-reconstruct.js';
import './demo-picker.js';
import './top-nav.js';
import './ecosystem-web.js';
import './layer-toggle.js';
import './logo-card.js';
import './nav-collapse.js';

// ── Lazy: d3 globe ───────────────────────────────────────────────────────────
// The globe pulls in d3-geo + the world atlas (~50 KiB gz), none of it needed
// for first paint. Load the module only once its canvas nears the viewport.
const globeEl = document.querySelector('canvas[data-globe]');
if (globeEl) {
  const io = new IntersectionObserver(
    (entries, obs) => {
      if (entries.some((e) => e.isIntersecting)) {
        obs.disconnect();
        import('./globe-d3.js');
      }
    },
    { rootMargin: '200px' },
  );
  io.observe(globeEl);
}

// ── Deferred (GSAP) ──────────────────────────────────────────────────────────
let motionLoaded = false;
function loadMotion() {
  if (motionLoaded) return;
  motionLoaded = true;
  import('./motion.js');
  import('./solutions-scroller.js');
  import('./chart-nodes-hover.js');
}

// Load on the user's first intent to scroll/interact so hovers + the solutions
// scroller are ready by the time they're reached; `once` auto-removes each.
['pointerdown', 'touchstart', 'wheel', 'keydown', 'scroll', 'mousemove'].forEach((evt) =>
  window.addEventListener(evt, loadMotion, { once: true, passive: true }),
);

// Fallback: if the user does nothing, load during idle time (with a timeout so
// it still loads on browsers that stay "busy"). Keeps below-the-fold reveals
// primed before the user scrolls to them.
if ('requestIdleCallback' in window) {
  requestIdleCallback(loadMotion, { timeout: 2500 });
} else {
  setTimeout(loadMotion, 1800);
}
